import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { allowedTransitions, formatMoney, nextStep, type OrderStatus } from '@johnsaks/core'
import { useRef, useState } from 'react'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { BtnGhost, BtnPrimary, Field, Select, TextInput } from '@/components/field'
import { Modal } from '@/components/modal'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'
import {
  centsFromDollars,
  fmtDateTime,
  ORDER_FLOW,
  ORDER_STATUS,
  PAYMENT_STATUS,
} from '@/lib/labels'

type OrderDetail = {
  reference: string
  status: string
  version: number
  paymentStatus?: string
  paidCents?: number
  source?: string
  mode: string
  contactName: string
  contactPhone: string | null
  contactEmail?: string | null
  totalCents: number
  subtotalCents: number
  deliveryCents: number
  discountCents: number
  manualDiscountCents?: number
  placedAt: string
  addressLine: string | null
  addressNote?: string | null
  items: Array<{
    id: string
    nameSnapshot: string
    skuSnapshot: string
    quantity: number
    unitPriceCents: number
  }>
  payments: Array<{
    id: string
    method: string
    status: string
    amountCents: number
    manualReference?: string | null
  }>
  statusEvents: Array<{
    id: string
    from: string | null
    to: string
    note: string | null
    createdAt: string
  }>
  customer: { id: string; email: string; firstName: string; lastName: string } | null
  commune?: { id: string; name: string } | null
  pickupPoint?: { id: string; name: string; code?: string; address?: string } | null
  parcel?: { handoverCode: string | null; status: string } | null
}

function destinationLabel(o: OrderDetail) {
  if (o.mode === 'HOME') {
    const parts = [o.addressLine, o.commune?.name].filter(Boolean)
    return parts.length ? parts.join(', ') : '—'
  }
  if (o.pickupPoint?.name) {
    const parts = [o.pickupPoint.name, o.commune?.name].filter(Boolean)
    return parts.join(' · ')
  }
  return o.commune?.name ?? '—'
}

export function CommandeDetailPage() {
  const { ref } = useParams({ strict: false }) as { ref: string }
  const toast = useToast()
  const qc = useQueryClient()
  const [pendingStatus, setPendingStatus] = useState<string | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [payOpen, setPayOpen] = useState(false)
  const [payAmount, setPayAmount] = useState('')
  const [payMethod, setPayMethod] = useState('BANK_TRANSFER')
  const [payRef, setPayRef] = useState('')
  const [editName, setEditName] = useState('')
  const [editPhone, setEditPhone] = useState('')
  const [editAddress, setEditAddress] = useState('')
  const [editDelivery, setEditDelivery] = useState('')
  const printRef = useRef<HTMLDivElement>(null)

  const order = useQuery({
    queryKey: ['admin-order', ref],
    queryFn: () => api<OrderDetail>(`/admin/orders/${ref}`),
    enabled: Boolean(ref),
  })

  const transition = useMutation({
    mutationFn: (to: string) =>
      to === 'CANCELLED'
        ? apiMutate(`/admin/orders/${ref}/cancel`, 'POST', { reason: 'staff' })
        : apiMutate(`/admin/orders/${ref}/transitions`, 'POST', { to }),
    onSuccess: () => {
      toast.ok('Statut mis à jour')
      setPendingStatus(null)
      void qc.invalidateQueries({ queryKey: ['admin-order', ref] })
      void qc.invalidateQueries({ queryKey: ['admin-orders'] })
      void qc.invalidateQueries({ queryKey: ['admin-badges'] })
      void qc.invalidateQueries({ queryKey: ['admin-dashboard'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const captureCod = useMutation({
    mutationFn: (paymentId: string) =>
      apiMutate(`/admin/orders/${ref}/payments/${paymentId}/capture-cod`, 'POST'),
    onSuccess: () => {
      toast.ok('COD encaissé')
      void qc.invalidateQueries({ queryKey: ['admin-order', ref] })
      void qc.invalidateQueries({ queryKey: ['admin-payments'] })
      void qc.invalidateQueries({ queryKey: ['admin-dashboard'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const confirmPay = useMutation({
    mutationFn: (paymentId: string) =>
      apiMutate(`/admin/orders/${ref}/payments/${paymentId}/confirm`, 'POST'),
    onSuccess: () => {
      toast.ok('Paiement confirmé')
      void qc.invalidateQueries({ queryKey: ['admin-order', ref] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const recordPay = useMutation({
    mutationFn: () =>
      apiMutate(`/admin/orders/${ref}/payments`, 'POST', {
        amountCents: centsFromDollars(payAmount),
        method: payMethod,
        manualReference: payRef || undefined,
        confirmImmediately: true,
      }),
    onSuccess: () => {
      toast.ok('Paiement enregistré')
      setPayOpen(false)
      setPayAmount('')
      setPayRef('')
      void qc.invalidateQueries({ queryKey: ['admin-order', ref] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const editOrder = useMutation({
    mutationFn: () =>
      apiMutate(`/admin/orders/${ref}`, 'PATCH', {
        expectedVersion: order.data!.version,
        contactName: editName || undefined,
        contactPhone: editPhone || undefined,
        addressLine: editAddress || undefined,
        deliveryCents: editDelivery !== '' ? centsFromDollars(editDelivery) : undefined,
        deliveryFeeOverridden: editDelivery !== '' ? true : undefined,
      }),
    onSuccess: () => {
      toast.ok('Commande mise à jour')
      setEditOpen(false)
      void qc.invalidateQueries({ queryKey: ['admin-order', ref] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  function printBordereau() {
    const node = printRef.current
    if (!node) return
    // Named window avoids noopener blanking document access in some browsers.
    const win = window.open('', 'js-bordereau', 'width=480,height=640')
    if (!win) {
      toast.err('Impossible d’ouvrir la fenêtre d’impression')
      return
    }
    win.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"/><title>Bordereau ${order.data?.reference ?? ''}</title>
<style>
  body { font-family: Georgia, "Times New Roman", serif; margin: 2rem; color: #151011; }
  h1 { font-size: 1.25rem; margin: 0 0 1.25rem; letter-spacing: 0.04em; text-transform: uppercase; }
  dl { display: grid; gap: 0.75rem; }
  dt { font-size: 0.7rem; letter-spacing: 0.12em; text-transform: uppercase; color: #6b6560; margin: 0; }
  dd { margin: 0.15rem 0 0; font-size: 1rem; }
  .note { margin-top: 2rem; padding-top: 1rem; border-top: 1px solid #ccc; font-size: 1.05rem; font-weight: 600; }
  @media print { body { margin: 1rem; } }
</style></head><body>${node.innerHTML}</body></html>`)
    win.document.close()
    win.focus()
    win.print()
  }

  function openEdit() {
    if (!order.data) return
    setEditName(order.data.contactName)
    setEditPhone(order.data.contactPhone ?? '')
    setEditAddress(order.data.addressLine ?? '')
    setEditDelivery((order.data.deliveryCents / 100).toFixed(2))
    setEditOpen(true)
  }

  if (order.isError) {
    return (
      <div className="carte-admin p-6">
        <p className="text-rouge-texte">Commande introuvable.</p>
        <a href="/commandes" className="mt-3 inline-block text-sm text-rouge">
          Retour
        </a>
      </div>
    )
  }

  if (order.isLoading || !order.data) {
    return <p className="text-second-admin">Chargement…</p>
  }

  const o = order.data
  const st = ORDER_STATUS[o.status] ?? { label: o.status, tone: 'neutral' }
  const paySt = o.paymentStatus
    ? (PAYMENT_STATUS[o.paymentStatus] ?? { label: o.paymentStatus, tone: 'neutral' })
    : null
  const next = nextStep(o.status as OrderStatus)
  const allowed = allowedTransitions(o.status as OrderStatus)
  const flowIndex = ORDER_FLOW.indexOf(o.status as (typeof ORDER_FLOW)[number])
  const dest = destinationLabel(o)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <a href="/commandes" className="kicker text-second-admin hover:text-encre-admin">
          ← Commandes
        </a>
        <h2 className="font-titre text-xl text-encre-admin">{o.reference}</h2>
        <StatusPill label={st.label} tone={st.tone} />
        {paySt ? <StatusPill label={paySt.label} tone={paySt.tone} /> : null}
        <div className="ml-auto flex flex-wrap gap-2">
          <BtnGhost onClick={printBordereau}>Bordereau</BtnGhost>
          <BtnGhost onClick={openEdit}>Éditer</BtnGhost>
          <BtnGhost onClick={() => setPayOpen(true)}>Paiement</BtnGhost>
          {next && allowed.includes(next) ? (
            <BtnPrimary onClick={() => setPendingStatus(next)}>
              Passer à {ORDER_STATUS[next]?.label ?? next}
            </BtnPrimary>
          ) : null}
          {allowed.includes('CANCELLED') ? (
            <BtnGhost onClick={() => setPendingStatus('CANCELLED')}>Annuler</BtnGhost>
          ) : null}
        </div>
      </div>

      <section className="carte-admin p-4">
        <p className="kicker text-second-admin mb-3">Progression</p>
        <ol className="flex flex-wrap gap-2">
          {ORDER_FLOW.map((s, i) => {
            const done = flowIndex >= i
            const current = o.status === s
            const label = ORDER_STATUS[s]?.label ?? s
            const can = allowed.includes(s as OrderStatus)
            return (
              <li key={s}>
                <button
                  type="button"
                  disabled={current || transition.isPending || !can}
                  onClick={() => setPendingStatus(s)}
                  className={`kicker rounded-pilule px-3 py-1.5 transition-opacity disabled:cursor-default ${
                    current
                      ? 'bg-encre-admin text-creme'
                      : done
                        ? 'bg-vert-fond text-vert-texte hover:opacity-80'
                        : 'bg-gris-fond text-second-admin hover:bg-gris-fond/80'
                  }`}
                  title={current || !can ? undefined : `Passer à ${label}`}
                >
                  {label}
                </button>
              </li>
            )
          })}
        </ol>
        {allowed.filter((s) => s !== next && s !== 'CANCELLED').length ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {allowed
              .filter((s) => s !== next)
              .map((s) => (
                <BtnGhost key={s} onClick={() => setPendingStatus(s)}>
                  {ORDER_STATUS[s]?.label ?? s}
                </BtnGhost>
              ))}
          </div>
        ) : null}
      </section>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="carte-admin p-4">
          <h3 className="text-base">Contact</h3>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-second-admin">Nom</dt>
              <dd>{o.contactName}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-second-admin">Téléphone</dt>
              <dd>{o.contactPhone ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-second-admin">E-mail</dt>
              <dd>{o.contactEmail ?? o.customer?.email ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-second-admin">Mode</dt>
              <dd>{o.mode === 'HOME' ? 'Domicile' : 'Point relais'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-second-admin">Destination</dt>
              <dd className="text-right">{dest}</dd>
            </div>
            {o.source ? (
              <div className="flex justify-between gap-4">
                <dt className="text-second-admin">Source</dt>
                <dd>{o.source}</dd>
              </div>
            ) : null}
            {o.parcel?.handoverCode ? (
              <div className="flex justify-between gap-4">
                <dt className="text-second-admin">Code remise</dt>
                <dd className="font-medium tabular-nums tracking-widest">{o.parcel.handoverCode}</dd>
              </div>
            ) : null}
            <div className="flex justify-between gap-4">
              <dt className="text-second-admin">Passée le</dt>
              <dd>{fmtDateTime(o.placedAt)}</dd>
            </div>
          </dl>
          {o.customer ? (
            <a
              href={`/clients/${o.customer.id}`}
              className="mt-3 inline-block text-xs tracking-[0.12em] text-rouge uppercase"
            >
              Fiche client
            </a>
          ) : null}
        </section>

        <section className="carte-admin p-4">
          <h3 className="text-base">Paiement</h3>
          {o.payments?.length ? (
            <ul className="mt-3 space-y-3">
              {o.payments.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 text-sm">
                  <span>
                    {p.method} · {p.status}
                    {p.manualReference ? ` · ${p.manualReference}` : ''}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="tabular-nums">{formatMoney(p.amountCents)}</span>
                    {p.method === 'CASH_ON_DELIVERY' && p.status === 'PENDING_MANUAL' ? (
                      <BtnPrimary
                        disabled={captureCod.isPending}
                        onClick={() => captureCod.mutate(p.id)}
                      >
                        Marquer COD encaissé
                      </BtnPrimary>
                    ) : null}
                    {p.method !== 'CASH_ON_DELIVERY' && p.status === 'PENDING_MANUAL' ? (
                      <BtnPrimary
                        disabled={confirmPay.isPending}
                        onClick={() => confirmPay.mutate(p.id)}
                      >
                        Confirmer
                      </BtnPrimary>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-second-admin">Aucun paiement enregistré.</p>
          )}
          <dl className="mt-4 space-y-2 border-t border-bordure-admin pt-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-second-admin">Sous-total</dt>
              <dd>{formatMoney(o.subtotalCents)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-second-admin">Livraison</dt>
              <dd>{formatMoney(o.deliveryCents)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-second-admin">Remise</dt>
              <dd>−{formatMoney(o.discountCents)}</dd>
            </div>
            <div className="flex justify-between font-medium">
              <dt>Total</dt>
              <dd>{formatMoney(o.totalCents)}</dd>
            </div>
            {o.paidCents != null ? (
              <div className="flex justify-between">
                <dt className="text-second-admin">Encaissé</dt>
                <dd>{formatMoney(o.paidCents)}</dd>
              </div>
            ) : null}
          </dl>
        </section>
      </div>

      <section className="carte-admin">
        <div className="border-b border-bordure-admin px-4 py-3">
          <h3 className="text-base">Articles</h3>
        </div>
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-bordure-admin">
              {['Produit', 'SKU', 'Qté', 'Prix', 'Total'].map((h) => (
                <th key={h} className="px-4 py-2.5">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {o.items.map((it) => (
              <tr key={it.id} className="border-b border-bordure-admin last:border-0">
                <td className="px-4 py-2.5">{it.nameSnapshot}</td>
                <td className="px-4 py-2.5 text-second-admin">{it.skuSnapshot}</td>
                <td className="px-4 py-2.5 tabular-nums">{it.quantity}</td>
                <td className="px-4 py-2.5 tabular-nums">{formatMoney(it.unitPriceCents)}</td>
                <td className="px-4 py-2.5 tabular-nums">
                  {formatMoney(it.unitPriceCents * it.quantity)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {o.statusEvents?.length ? (
        <section className="carte-admin p-4">
          <h3 className="text-base">Historique</h3>
          <ul className="mt-3 space-y-2 text-sm">
            {o.statusEvents.map((e) => (
              <li key={e.id} className="flex justify-between gap-3">
                <span>
                  {e.from ? `${ORDER_STATUS[e.from]?.label ?? e.from} → ` : ''}
                  {ORDER_STATUS[e.to]?.label ?? e.to}
                  {e.note ? ` · ${e.note}` : ''}
                </span>
                <span className="shrink-0 text-second-admin">{fmtDateTime(e.createdAt)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="sr-only" aria-hidden="true">
        <div ref={printRef}>
          <h1>Bordereau de livraison</h1>
          <dl>
            <div>
              <dt>Référence</dt>
              <dd>{o.reference}</dd>
            </div>
            <div>
              <dt>Contact</dt>
              <dd>{o.contactName}</dd>
            </div>
            <div>
              <dt>Téléphone</dt>
              <dd>{o.contactPhone ?? '—'}</dd>
            </div>
            <div>
              <dt>{o.mode === 'HOME' ? 'Adresse' : 'Point relais'}</dt>
              <dd>{dest}</dd>
            </div>
          </dl>
          <p className="note">Remise contre code</p>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(pendingStatus)}
        title="Changer le statut ?"
        body={
          pendingStatus
            ? `La commande passera à « ${ORDER_STATUS[pendingStatus]?.label ?? pendingStatus} ».`
            : ''
        }
        confirmLabel="Confirmer"
        danger={pendingStatus === 'CANCELLED' || pendingStatus === 'DELIVERY_INCIDENT'}
        busy={transition.isPending}
        onClose={() => setPendingStatus(null)}
        onConfirm={() => pendingStatus && transition.mutate(pendingStatus)}
      />

      <Modal
        open={editOpen}
        title="Éditer la commande"
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <BtnGhost onClick={() => setEditOpen(false)}>Annuler</BtnGhost>
            <BtnPrimary disabled={editOrder.isPending} onClick={() => editOrder.mutate()}>
              Enregistrer
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Contact">
            <TextInput value={editName} onChange={(e) => setEditName(e.target.value)} />
          </Field>
          <Field label="Téléphone">
            <TextInput value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
          </Field>
          <Field label="Adresse">
            <TextInput value={editAddress} onChange={(e) => setEditAddress(e.target.value)} />
          </Field>
          <Field label="Frais livraison ($)">
            <TextInput value={editDelivery} onChange={(e) => setEditDelivery(e.target.value)} />
          </Field>
          <p className="text-xs text-second-admin">Version {o.version}</p>
        </div>
      </Modal>

      <Modal
        open={payOpen}
        title="Paiement manuel"
        onClose={() => setPayOpen(false)}
        footer={
          <>
            <BtnGhost onClick={() => setPayOpen(false)}>Annuler</BtnGhost>
            <BtnPrimary disabled={recordPay.isPending} onClick={() => recordPay.mutate()}>
              Enregistrer
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Montant ($)">
            <TextInput
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              placeholder={(o.totalCents / 100).toFixed(2)}
            />
          </Field>
          <Field label="Moyen">
            <Select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
              <option value="BANK_TRANSFER">Virement</option>
              <option value="ORANGE_MONEY">Orange Money</option>
              <option value="MPESA">M-Pesa</option>
              <option value="AIRTEL_MONEY">Airtel Money</option>
              <option value="CASH_ON_DELIVERY">COD</option>
            </Select>
          </Field>
          <Field label="Référence">
            <TextInput value={payRef} onChange={(e) => setPayRef(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}
